import { cn } from '../lib/cn';
import type { SegmentedType } from '../types/global.types';
import ModuleGuard from './Guards/ModuleGuard';

type Props<T extends string> = {
  selected: T;
  config: SegmentedType<T>[];
  onChange: (value: T) => void;
  className?: string;
};

const CustomSegment = <T extends string>({
  config,
  selected,
  onChange,
  className,
}: Props<T>) => {
  return (
    <div
      className={cn(
        // layout
        'flex items-center overflow-x-auto xl:overflow-visible hide-scrollbar',
        // spacing
        'mt-6 md:mt-0 px-1 mx-0 gap-2.5 md:gap-7',
        // border & background
        'bg-white dark:bg-theme-slate-900 md:border-b md:border-white/70',
        className,
      )}
    >
      {config.map((item) => {
        const isActive = selected === item.value;

        return (
          <ModuleGuard
            key={item.value}
            checkFor="route"
            moduleId={item?.moduleId === '' ? '' : item?.moduleId ?? 'default'}
          >
            <button
              key={item.value}
              onClick={() => onChange(item.value)}
              className={cn(
                // layout & spacing
                'relative p-3 py-2 md:py-3 rounded-lg md:rounded-none',
                // typography
                'text-base text-nowrap xl:text-wrap transition-colors duration-300 text-center xl:text-start',
                // border
                'border md:border-0',
                // active / inactive styles
                isActive
                  ? 'font-medium text-theme-primary dark:text-white border-white'
                  : 'font-normal text-gray-600 dark:text-white border-theme-gray-700 hover:text-theme-primary',
              )}
            >
              {item.label}

              {/* Bottom bar indicator (tablet & desktop only) */}
              <span
                className={cn(
                  // layout
                  'hidden md:block absolute left-0 right-0 -bottom-[2.5px] h-[5px] rounded-full',
                  // animation
                  'transition-transform duration-300 origin-center',
                  // color
                  'bg-theme-primary dark:bg-white',
                  isActive ? 'scale-x-100' : 'scale-x-0',
                )}
              />
            </button>
          </ModuleGuard>
        );
      })}
    </div>
  );
};

export default CustomSegment;
